import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../context/AuthContext';

interface Supplier {
  id: number; business_no: string; company_name: string;
  contact_name?: string; phone?: string; email?: string;
  address?: string; memo?: string; is_active: number; created_at: string;
}

const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
      <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
        <h2 className="font-bold text-stone-900">{title}</h2>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-2xl leading-none">×</button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

const blankForm = { business_no: '', company_name: '', contact_name: '', phone: '', email: '', address: '', memo: '' };

export default function VendorsPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [modal,     setModal]     = useState<'add' | 'edit' | 'delete' | null>(null);
  const [target,    setTarget]    = useState<Supplier | null>(null);
  const [form,      setForm]      = useState(blankForm);
  const [saving,    setSaving]    = useState(false);
  const [errMsg,    setErrMsg]    = useState('');
  const [toast,     setToast]     = useState('');

  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3500); };
  const close  = () => { setModal(null); setTarget(null); setErrMsg(''); };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await authFetch(`/api/suppliers${search ? '?search=' + encodeURIComponent(search) : ''}`);
    if (res.ok) setSuppliers(await res.json());
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(blankForm); setErrMsg(''); setModal('add'); };
  const openEdit = (s: Supplier) => {
    setTarget(s);
    setForm({ business_no: s.business_no, company_name: s.company_name, contact_name: s.contact_name ?? '',
              phone: s.phone ?? '', email: s.email ?? '', address: s.address ?? '', memo: s.memo ?? '' });
    setErrMsg('');
    setModal('edit');
  };
  const openDel  = (s: Supplier) => { setTarget(s); setModal('delete'); };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setErrMsg('');
    const url    = target ? `/api/suppliers/${target.id}` : '/api/suppliers';
    const method = target ? 'PUT' : 'POST';
    const res    = await authFetch(url, { method, body: JSON.stringify(form) });
    const data   = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '저장 실패'); return; }
    await load();
    flash(target ? '거래처 정보가 수정되었습니다.' : '거래처가 등록되었습니다.');
    close();
  };

  const handleDelete = async () => {
    if (!target) return;
    setSaving(true);
    const res  = await authFetch(`/api/suppliers/${target.id}`, { method: 'DELETE' });
    const data = await res.json();
    setSaving(false);
    await load();
    flash(data.note ?? '처리 완료');
    close();
  };

  const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <label className="block text-xs font-semibold text-stone-500 mb-1">{label}</label>
      {children}
    </div>
  );
  const inp = (field: keyof typeof blankForm, placeholder: string, required?: boolean) => (
    <input value={form[field]} onChange={e => setForm({...form, [field]: e.target.value})} required={required}
      placeholder={placeholder}
      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">거래처 관리</h1>
          <p className="text-stone-500 text-sm mt-1">자재 공급업체 등록 및 관리</p>
        </div>
        <button onClick={openAdd} className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm transition flex items-center gap-2">
          <span className="text-lg leading-none">+</span> 거래처 추가
        </button>
      </div>

      {toast && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">{toast}</div>
      )}

      {/* 검색 */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          placeholder="회사명, 사업자번호, 담당자명 검색..." />
      </div>

      {/* 테이블 */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-100 text-xs font-semibold text-stone-500">
              <th className="text-left px-5 py-3">사업자번호</th>
              <th className="text-left px-5 py-3">회사명</th>
              <th className="text-left px-5 py-3">담당자</th>
              <th className="text-left px-5 py-3">연락처</th>
              <th className="text-center px-5 py-3">상태</th>
              <th className="text-center px-5 py-3">관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-16 text-stone-400 animate-pulse">불러오는 중...</td></tr>
            ) : suppliers.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-16 text-stone-400">등록된 거래처가 없습니다.</td></tr>
            ) : suppliers.map(s => (
              <tr key={s.id} className={`border-b border-stone-50 transition hover:bg-stone-50 ${!s.is_active ? 'opacity-50' : ''}`}>
                <td className="px-5 py-3.5 text-sm font-mono text-stone-600">{s.business_no}</td>
                <td className="px-5 py-3.5">
                  <p className="font-semibold text-stone-800 text-sm">{s.company_name}</p>
                  {s.memo && <p className="text-xs text-stone-400 truncate max-w-xs">{s.memo}</p>}
                </td>
                <td className="px-5 py-3.5 text-sm text-stone-600">{s.contact_name || '-'}</td>
                <td className="px-5 py-3.5 text-sm text-stone-600">{s.phone || '-'}</td>
                <td className="px-5 py-3.5 text-center">
                  {s.is_active
                    ? <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-700">활성</span>
                    : <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-stone-100 text-stone-500">비활성</span>}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-center gap-1.5">
                    <button onClick={() => openEdit(s)} title="수정"
                      className="p-1.5 rounded hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => openDel(s)} title="삭제"
                      className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 추가/수정 모달 */}
      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'add' ? '거래처 추가' : '거래처 수정'} onClose={close}>
          <form onSubmit={handleSave} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="사업자번호 *">{inp('business_no', '000-00-00000', true)}</FieldRow>
              <FieldRow label="회사명 *">{inp('company_name', '(주)회사명', true)}</FieldRow>
              <FieldRow label="담당자">{inp('contact_name', '홍길동')}</FieldRow>
              <FieldRow label="연락처">{inp('phone', '02-0000-0000')}</FieldRow>
              <FieldRow label="이메일"><input value={form.email} onChange={e => setForm({...form, email: e.target.value})} type="email" placeholder="contact@company.com"
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" /></FieldRow>
              <FieldRow label="주소">{inp('address', '서울시 ...')}</FieldRow>
            </div>
            <FieldRow label="메모">
              <textarea value={form.memo} onChange={e => setForm({...form, memo: e.target.value})} rows={2}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                placeholder="특이사항 (선택)" />
            </FieldRow>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm">
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* 삭제 모달 */}
      {modal === 'delete' && target && (
        <Modal title="거래처 삭제" onClose={close}>
          <div className="text-center py-2">
            <div className="text-5xl mb-4">오류</div>
            <p className="font-semibold text-stone-800 mb-1">{target.company_name}</p>
            <p className="text-stone-500 text-sm mb-6">입고 이력이 있으면 비활성화로 처리됩니다.</p>
            <div className="flex gap-3">
              <button onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm">
                {saving ? '처리 중...' : '삭제'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
